import dayjs from 'dayjs';
import { Job, JobStatus, UserRole } from '../generated/gql-types';
import { ICoreLoopConfigurationItems } from '../coreLoopConfiguration/coreLoopConfiguration.types';

/**
 * Merges two arrays of jobs, handling duplicates by using the most recent data
 * @param existingJobs Current list of jobs
 * @param newJobs New jobs to merge
 * @param strategy 'replace' to replace existing jobs, 'keep' to keep existing jobs (default: 'replace')
 * @returns Merged array of jobs with duplicates handled
 */
export const mergeJobLists = (
  existingJobs: Job[],
  newJobs: Job[],
  strategy: 'replace' | 'keep' = 'replace'
): Job[] => {
  // Create a map of existing jobs by ID for faster lookup
  const jobMap = new Map<string, Job>();

  // First, add all existing jobs to the map
  existingJobs.forEach((job) => {
    if (job?.id) {
      jobMap.set(job.id, job);
    }
  });

  // Then merge in new jobs based on strategy
  newJobs.forEach((newJob) => {
    if (!newJob?.id) return;

    const existingJob = jobMap.get(newJob.id);
    if (!existingJob) {
      // If job doesn't exist, add it
      jobMap.set(newJob.id, newJob);
    } else if (strategy === 'replace') {
      // If job exists and strategy is replace, update with new data
      jobMap.set(newJob.id, {
        ...existingJob,
        ...newJob,
      });
    }
    // If strategy is 'keep', we keep the existing job
  });

  // Convert map back to array
  return Array.from(jobMap.values());
};

/**
 * Updates a single job in a list of jobs
 * @param jobs List of jobs
 * @param updatedJob Job with updated data
 * @returns New array with the updated job
 */
export const updateJobInList = (jobs: Job[], updatedJob: Job): Job[] => {
  if (!updatedJob?.id) return jobs;

  return jobs.map((job) =>
    job.id === updatedJob.id ? { ...job, ...updatedJob } : job
  );
};

/**
 * Removes a job from a list of jobs
 * @param jobs List of jobs
 * @param jobId ID of the job to remove
 * @returns New array without the removed job
 */
export const removeJobFromList = (jobs: Job[], jobId: string): Job[] => {
  return jobs.filter((job) => job.id !== jobId);
};

/**
 * Checks if a job is in the past
 * @param job Job to check
 * @returns True if the job is in the past, false otherwise
 */
export const isJobInPast = (job: Job): boolean => {
  const startDate = job?.startDate;

  // If the job has no end date, it is not in the past.
  if (!startDate) return false;

  const startTime = parseInt(startDate, 10);

  // If the end date is not a valid number, it is not in the past.
  if (Number.isNaN(startTime)) return false;

  return startTime < Date.now();
};

/**
 * Checks if a job is active today.
 * A job is active today if it starts or ends today or continues today.
 *
 * @param job Job to check if it is active today
 * @returns True if the job is active today, false otherwise
 */
export const isJobActiveToday = (job: Job): boolean => {
  if (
    !job.startDate ||
    !job.endDate ||
    isNaN(Number(job.startDate)) ||
    isNaN(Number(job.endDate))
  ) {
    return false;
  }

  const startsToday = dayjs(Number(job.startDate)).isSame(dayjs(), 'day');
  const endsToday = dayjs(Number(job.endDate)).isSame(dayjs(), 'day');
  const continuesToday =
    dayjs(Number(job.startDate)).isBefore(dayjs(), 'day') &&
    dayjs(Number(job.endDate)).isAfter(dayjs(), 'day');

  return startsToday || endsToday || continuesToday;
};

/** Defaults match backend CoreLoopConfiguration defaults when JSON omits keys. */
const defaultClientsCanCancelFilledJob = true;
const defaultMinimumBufferForClientCancellingFilledJobInMinutes = 120;

/**
 * Mirrors backend `Job.cancelJobValidation` for filled jobs (core loop branch).
 * When true, do not allow the client to cancel the job.
 *
 * clientsCanCancelFilledJob - If false, do not allow the client to cancel the job.
 *
 * minimumBufferForClientCancellingFilledJobInMinutes - If the job is within this number of
 * minutes, do not allow the client to cancel the job.
 *
 */
export const isCancelFilledJobBlockedByCoreLoop = (
  job: Job,
  coreLoopConfigurationItems: ICoreLoopConfigurationItems | null,
  userRole: UserRole | undefined
): boolean => {
  if (job.itemStatus !== JobStatus.Filled) {
    return false;
  }

  if (!coreLoopConfigurationItems) {
    return false;
  }

  const clientsCanCancelFilledJob =
    coreLoopConfigurationItems?.clientsCanCancelFilledJob ??
    defaultClientsCanCancelFilledJob;

  const minimumBufferMinutes =
    coreLoopConfigurationItems?.minimumBufferForClientCancellingFilledJobInMinutes ??
    defaultMinimumBufferForClientCancellingFilledJobInMinutes;

  if (
    !clientsCanCancelFilledJob &&
    (userRole === UserRole.ClientAdmin || userRole === UserRole.ClientUser)
  ) {
    return true;
  }

  if (job.startDate) {
    const startTimestamp = parseInt(job.startDate, 10);
    if (!Number.isNaN(startTimestamp)) {
      const startDate = dayjs(startTimestamp);
      const now = dayjs();
      const minutesUntilStart = startDate.diff(now, 'minutes');
      if (minutesUntilStart < minimumBufferMinutes) {
        return true;
      }
    }
  }

  return false;
};
